import React, { useState } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Pencil, Plus, Check, X, Briefcase } from "lucide-react";

const ExperienceSection = ({ timeline = [], onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [experiences, setExperiences] = useState(timeline);

  const handleSave = () => {
    onUpdate(experiences);
    setIsEditing(false);
  };

  const handleAddExperience = () => {
    setExperiences([
      ...experiences,
      {
        id: Date.now(),
        role: '',
        company: '',
        period: '',
        description: '',
        achievements: [],
        isMilestone: false
      }
    ]);
  };

  const handleUpdateExperience = (index, field, value) => {
    const updatedExperiences = [...experiences];
    updatedExperiences[index] = {
      ...updatedExperiences[index],
      [field]: value
    };
    setExperiences(updatedExperiences);
  };

  const handleRemoveExperience = (index) => {
    setExperiences(experiences.filter((_, i) => i !== index));
  };

  return (
    <Card className="p-6 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold dark:text-white">Experience</h2>
        {!isEditing && (
          <Button variant="ghost" size="sm" onClick={() => setIsEditing(true)}>
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
        )}
      </div>

      {isEditing ? (
        <div className="space-y-4">
          {experiences.map((exp, index) => (
            <div key={exp.id} className="p-4 border rounded-lg dark:border-gray-700">
              <div className="space-y-4">
                <div className="space-y-2">
                  <input
                    className="w-full p-2 rounded border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={exp.role}
                    onChange={(e) => handleUpdateExperience(index, 'role', e.target.value)}
                    placeholder="Role/Position"
                  />
                  <input
                    className="w-full p-2 rounded border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={exp.company}
                    onChange={(e) => handleUpdateExperience(index, 'company', e.target.value)}
                    placeholder="Company"
                  />
                  <input
                    className="w-full p-2 rounded border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={exp.period}
                    onChange={(e) => handleUpdateExperience(index, 'period', e.target.value)}
                    placeholder="Period (e.g., 2020 - Present)"
                  />
                  <textarea
                    className="w-full p-2 rounded border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                    value={exp.description}
                    onChange={(e) => handleUpdateExperience(index, 'description', e.target.value)}
                    placeholder="Description"
                    rows="3"
                  />
                  <div className="space-y-2">
                    <label className="block text-sm font-medium dark:text-gray-300">Achievements</label>
                    {exp.achievements?.map((achievement, achIndex) => (
                      <div key={achIndex} className="flex gap-2">
                        <input
                          className="flex-1 p-2 rounded border dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                          value={achievement}
                          onChange={(e) => {
                            const newAchievements = [...exp.achievements];
                            newAchievements[achIndex] = e.target.value;
                            handleUpdateExperience(index, 'achievements', newAchievements);
                          }}
                          placeholder={`Achievement ${achIndex + 1}`}
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            const newAchievements = exp.achievements.filter((_, i) => i !== achIndex);
                            handleUpdateExperience(index, 'achievements', newAchievements);
                          }}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newAchievements = [...(exp.achievements || []), ''];
                        handleUpdateExperience(index, 'achievements', newAchievements);
                      }}
                    >
                      <Plus className="w-4 h-4 mr-2" /> Add Achievement
                    </Button>
                  </div>
                </div>
                <Button 
                  variant="destructive" 
                  size="sm"
                  onClick={() => handleRemoveExperience(index)}
                >
                  Remove Experience
                </Button>
              </div>
            </div>
          ))}
          
          <div className="flex justify-between mt-4">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleAddExperience}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Experience
            </Button>
            <div className="space-x-2">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative space-y-0">
          {/* Continuous vertical line with extended edges */}
          <div 
            className="absolute left-[5px] top-[12px] bottom-[12px] w-[1px] bg-gray-200 dark:bg-gray-600"
            style={{
              background: 'linear-gradient(180deg, transparent 0%, #e5e7eb 8%, #e5e7eb 92%, transparent 100%)'
            }}
          />
          
          {timeline.map((exp, index) => (
            <div 
              key={exp.id} 
              className="relative pl-8"
              // Dynamic spacing based on content
              style={{
                marginBottom: exp.description || exp.achievements?.length > 0 ? '2.5rem' : '2rem'
              }}
            >
              {/* Timeline dot with perfect centering */}
              <div className="absolute left-0 top-[6px] flex items-center justify-center">
                <div className="w-[9px] h-[9px] rounded-full bg-blue-500 ring-2 ring-white dark:ring-gray-800" />
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg font-semibold dark:text-white leading-tight">
                  {exp.role}
                </h3>
                <div className="text-gray-600 dark:text-gray-300">
                  <span>{exp.company}</span>
                  {exp.period && <span className="mx-2">•</span>}
                  <span className="text-sm">{exp.period}</span>
                </div>
                {exp.description && (
                  <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                    {exp.description}
                  </p>
                )}
                {exp.achievements?.length > 0 && (
                  <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 ml-4 space-y-1">
                    {exp.achievements.map((achievement, i) => (
                      <li key={i} className="leading-relaxed">{achievement}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
          {timeline.length === 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-center py-4 relative z-10">
              No experience added yet.
            </p>
          )}
        </div>
      )}
    </Card>
  );
};

export default ExperienceSection;
